import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import { useDataService } from '@/lib/service-context';
import { useSalesDocuments, useSalesDocumentCount, useDeleteSalesDocument, useCreateSalesDocument, useMarkAsInvoiced } from '@/hooks/use-sales';
import { usePayments } from '@/hooks/use-payments';
import { computePaymentStatus, type PaymentStatus } from '@erp/domain/payments';
import { PaymentStatusBadge } from '@/components/PaymentStatusBadge';
import { useClients } from '@/hooks/use-clients';
import { useRepresentatives } from '@/hooks/use-representatives';
import { useArticles } from '@/hooks/use-articles';
import { useTableColumns } from '@/hooks/use-table-columns';
import { useCompanySettings } from '@/hooks/use-settings';
import { usePdfTemplates } from '@/hooks/use-pdf-templates';
import { activeTemplatesFor, companyInfoForPdf, printBatchWithTemplate } from '@/pages/settings/print-with-template';
import { resolveDocumentCustomer } from './document-customer';
import {
  calculateDocumentTotals,
  calculateLineTotals,
  calculateSalesDocumentBenefit,
} from '@erp/domain/sales';
import type { SalesDocument, SalesDocType, Client, Article } from '@erp/data-access/interfaces';
import {
  Badge, Button, Card, CardContent, CardHeader, Checkbox, DatePicker,
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
  EmptyState, FilterBar, FilterField, Input, PageHeader, Separator,
  Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@erp/ui';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ColumnTextFilterFields, useColumnTextFilters, type ColumnTextFilter } from '@/components/ui/column-text-filters';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import {
  Plus, Search, MoreHorizontal, Pencil, Trash2, ArrowRight, Receipt,
  AlertTriangle, FileText, Printer, ChevronLeft, ChevronRight,
} from '@/components/icons';
import { useCreateCertificateFromDocument } from '@/pages/withholding/use-create-certificate-from-document';
import { ColumnConfigButton, ColumnConfigDialog } from '@/components/ui/column-config';

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function formatDate(d: string): string {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

function formatDateTime(d: string): string {
  return formatDate(d.slice(0, 10));
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

const DOC_TYPE_LABELS: Record<SalesDocType, string> = {
  quote: 'sales.quote',
  order: 'sales.order',
  delivery: 'sales.delivery',
  invoice: 'sales.invoice',
  credit_note: 'sales.creditNote',
};
const STAMP_AMOUNT = 1; // Timbre fiscal obligatoire sur les factures.
const DOCUMENT_LIST_PAGE_SIZE = 80;

const DOC_TYPE_PLURAL_LABELS: Record<SalesDocType, string> = {
  quote: 'sales.quotes',
  order: 'sales.orders',
  delivery: 'sales.deliveries',
  invoice: 'sales.invoices',
  credit_note: 'sales.creditNotes',
};

function isNumericColumn(key: string) {
  return ['totalHt', 'totalDiscount', 'totalVat', 'totalTtc', 'withholdingAmount', 'stampAmount', 'deliveryFee', 'netToPay', 'benefit'].includes(key);
}

function isCenterColumn(key: string) {
  return key === 'isInvoiced' || key === 'paymentStatus' || key === 'channel';
}

const TRANSFORM_TARGETS: Record<SalesDocType, SalesDocType[]> = {
  quote: ['order', 'delivery'],
  order: ['delivery'],
  delivery: ['invoice', 'credit_note'],
  invoice: ['credit_note'],
  credit_note: [],
};

function calculateDocumentBenefit(doc: SalesDocument, articleMap: Map<string, Article>) {
  return calculateSalesDocumentBenefit((doc.lines ?? []).map((line) => {
    const catalogArticle = line.articleId ? articleMap.get(line.articleId) : undefined;
    return {
      articleId: line.articleId,
      quantity: line.quantity,
      unitPriceHt: line.unitPriceHt,
      discountPct: line.discountPct,
      // Frozen snapshot first; the catalog (net of supplier discount) is only a
      // fallback when the line never captured a cost.
      unitCostHt: line.purchasePrice,
      purchasePriceHt: catalogArticle?.purchasePriceHt,
      supplierDiscount: catalogArticle?.supplierDiscount,
    };
  }));
}

interface SalesDocumentListProps {
  docType: SalesDocType;
  basePath: string;
}

export function SalesDocumentList({ docType, basePath }: SalesDocumentListProps) {
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const ds = useDataService();
  const [searchNumber, setSearchNumber] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<SalesDocument | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [groupInvoiceOpen, setGroupInvoiceOpen] = useState(false);
  const [colConfigOpen, setColConfigOpen] = useState(false);
  const [groupInvoicing, setGroupInvoicing] = useState(false);
  const [groupKeepNonInvoiceable, setGroupKeepNonInvoiceable] = useState(false);
  const [filterInvoiced, setFilterInvoiced] = useState('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('');
  const debouncedSearchNumber = useDebouncedValue(searchNumber.trim(), 180);
  const [page, setPage] = useState(1);
  const { visibleKeys, toggleColumn, moveColumn, resetToDefault, applicableColumns } = useTableColumns(docType);
  const showInvoicedFilter = visibleKeys.includes('isInvoiced');
  const showPaymentStatusFilter = visibleKeys.includes('paymentStatus');
  const showBenefitColumn = visibleKeys.includes('benefit');

  const countFilters = useMemo(() => ({
    docType,
    search: debouncedSearchNumber || undefined,
    clientId: filterClientId || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    isInvoiced: showInvoicedFilter && filterInvoiced ? filterInvoiced === 'true' : undefined,
    paymentStatus: showPaymentStatusFilter && filterPaymentStatus ? filterPaymentStatus as PaymentStatus : undefined,
  }), [dateFrom, dateTo, debouncedSearchNumber, docType, filterClientId, filterInvoiced, filterPaymentStatus, showInvoicedFilter, showPaymentStatusFilter]);
  const documentFilters = useMemo(() => ({
    ...countFilters,
    includeLines: showBenefitColumn || undefined,
    offset: (page - 1) * DOCUMENT_LIST_PAGE_SIZE,
    limit: DOCUMENT_LIST_PAGE_SIZE,
  }), [countFilters, page, showBenefitColumn]);
  const { data: documents, isLoading, isFetching, isError } = useSalesDocuments(documentFilters);
  const {
    data: totalDocumentCount,
    isLoading: isCountLoading,
    isFetching: isCountFetching,
    isError: isCountError,
  } = useSalesDocumentCount(countFilters);
  const { data: clients } = useClients();
  const { data: representatives } = useRepresentatives();
  const { data: articles } = useArticles({ limit: 1000 });
  const { data: allPayments } = usePayments(showPaymentStatusFilter ? {} : undefined);
  const { data: companySettings } = useCompanySettings();
  const { data: pdfTemplates } = usePdfTemplates();
  const activeTemplates = useMemo(
    () => activeTemplatesFor(pdfTemplates, 'sales', docType),
    [pdfTemplates, docType],
  );
  const [bulkPrinting, setBulkPrinting] = useState(false);

  const allocatedByDocId = useMemo(() => {
    const map = new Map<string, number>();
    (allPayments ?? []).forEach((p) => {
      (p.allocations ?? []).forEach((a) => {
        if (a.documentType !== docType) return;
        map.set(a.documentId, (map.get(a.documentId) ?? 0) + Number(a.allocatedAmount || 0));
      });
    });
    return map;
  }, [allPayments, docType]);
  const deleteMutation = useDeleteSalesDocument();
  const createMutation = useCreateSalesDocument();
  const markInvoicedMutation = useMarkAsInvoiced();

  useEffect(() => {
    setPage(1);
  }, [countFilters]);

  // Group invoicing is only for deliveries. Multi-select (for bulk print)
  // is available for all doc types.
  const canGroupInvoice = docType === 'delivery';
  const showSelectionColumn = true;

  const clientMap = useMemo(() => {
    const map = new Map<string, Client>();
    clients?.forEach((c) => map.set(c.id, c));
    return map;
  }, [clients]);

  const { createCertificate, ConfirmationDialog: CertificateConfirmationDialog } = useCreateCertificateFromDocument();

  const articleMap = useMemo(() => {
    const map = new Map<string, Article>();
    articles?.forEach((a) => map.set(a.id, a));
    return map;
  }, [articles]);

  const representativeMap = useMemo(() => {
    const map = new Map<string, string>();
    representatives?.forEach((rep) => map.set(rep.id, rep.name));
    return map;
  }, [representatives]);

  const pageDocuments = documents ?? [];
  const totalDocuments = totalDocumentCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalDocuments / DOCUMENT_LIST_PAGE_SIZE));
  const pageStart = totalDocuments === 0 ? 0 : (page - 1) * DOCUMENT_LIST_PAGE_SIZE + 1;
  const pageEnd = Math.min(page * DOCUMENT_LIST_PAGE_SIZE, totalDocuments);
  const isCheckingDocuments = (isLoading && documents === undefined) || (isCountLoading && totalDocumentCount === undefined);
  const isWaitingForPageDocuments = totalDocuments > 0 && pageDocuments.length === 0 && isFetching;
  const hasPageDataMismatch = totalDocuments > 0 && pageDocuments.length === 0 && !isLoading && !isFetching;
  const hasLoadError = isError || isCountError || hasPageDataMismatch;

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const visibleColumns = useMemo(
    () => visibleKeys
      .map((key) => applicableColumns.find((c) => c.key === key))
      .filter((col) => companySettings?.showClientCarNumber !== false || col?.key !== 'carRegistrationNumber')
      .filter(Boolean) as typeof applicableColumns,
    [applicableColumns, companySettings?.showClientCarNumber, visibleKeys],
  );

  const salesColumnFilters = useMemo<ColumnTextFilter<SalesDocument>[]>(() => (
    visibleColumns.map((col) => ({
      key: col.key,
      label: t(col.labelKey),
      className: isNumericColumn(col.key) ? 'text-end' : '',
      getValue: (doc) => {
        const client = clientMap.get(doc.clientId);
        const documentCustomer = resolveDocumentCustomer(doc, clients);
        switch (col.key) {
          case 'number':
            return doc.number;
          case 'client':
            return [documentCustomer?.name, documentCustomer?.sourceLabel, client?.name, client?.code].filter(Boolean).join(' ');
          case 'representative':
            return doc.representativeId ? representativeMap.get(doc.representativeId) ?? doc.representativeId : '';
          case 'carRegistrationNumber':
            return doc.carRegistrationNumber ?? client?.carRegistrationNumber ?? '';
          case 'docDate':
            return formatDate(doc.docDate);
          case 'validityDate':
            return doc.validityDate ? formatDate(doc.validityDate) : '';
          case 'parentDoc': {
            const parentIds = doc.parentDocIds?.length ? doc.parentDocIds : doc.parentDocId ? [doc.parentDocId] : [];
            return parentIds.join(' ');
          }
          case 'channel':
            return doc.channel ? `${t(`sales.channels.${doc.channel}`, { defaultValue: doc.channel })} ${doc.channel}` : '';
          case 'proCommandeCode':
            return doc.proCommandeCode ?? '';
          case 'totalHt':
            return doc.totalHt;
          case 'totalDiscount':
            return doc.totalDiscount;
          case 'totalVat':
            return doc.totalVat;
          case 'totalTtc':
            return doc.totalTtc;
          case 'withholdingAmount':
            return doc.withholdingAmount;
          case 'stampAmount':
            return doc.stampAmount;
          case 'deliveryFee':
            return doc.deliveryFee;
          case 'netToPay':
            return doc.netToPay;
          case 'isInvoiced':
            return doc.isInvoiced ? t('sales.isInvoiced') : t('sales.notInvoiced');
          case 'paymentStatus': {
            const allocated = allocatedByDocId.get(doc.id) ?? 0;
            return computePaymentStatus(doc.netToPay ?? doc.totalTtc, allocated);
          }
          case 'benefit': {
            const benefit = calculateDocumentBenefit(doc, articleMap);
            return benefit.hasKnownPurchasePrice ? benefit.totalBenefit : '';
          }
          case 'notes':
            return doc.notes ?? '';
          case 'createdAt':
            return formatDateTime(doc.createdAt);
          case 'updatedAt':
            return formatDateTime(doc.updatedAt);
          default:
            return '';
        }
      },
    }))
  ), [allocatedByDocId, articleMap, clientMap, clients, representativeMap, t, visibleColumns]);

  const {
    filters: columnFilters,
    setFilter: setColumnFilter,
    filteredRows: filtered,
    hasColumnFilters,
    clearColumnFilters,
  } = useColumnTextFilters(pageDocuments, salesColumnFilters);

  const toolbarColumnFilters = useMemo(
    () => salesColumnFilters.filter((column) => ['representative', 'channel'].includes(column.key)),
    [salesColumnFilters],
  );

  // Selected documents for group invoicing
  const selectedDocs = useMemo(
    () => filtered.filter((d) => selected.has(d.id)),
    [filtered, selected],
  );

  // Validate group selection: same client, not already invoiced
  const groupValidation = useMemo(() => {
    if (selectedDocs.length < 2) return { valid: false, reason: 'sales.groupInvoice.needTwo' };
    const clientIds = new Set(selectedDocs.map((d) => d.clientId));
    if (clientIds.size > 1) return { valid: false, reason: 'sales.groupInvoice.sameClient' };
    if (selectedDocs.some((d) => d.isInvoiced)) return { valid: false, reason: 'sales.groupInvoice.alreadyInvoiced' };
    return { valid: true, reason: '' };
  }, [selectedDocs]);

  const handleDelete = async () => {
    if (toDelete) { await deleteMutation.mutateAsync(toDelete.id); setDeleteOpen(false); }
  };

  const handleTransform = (doc: SalesDocument, targetType: SalesDocType) => {
    navigate(`${getBasePath(targetType)}/new?from=${doc.id}`);
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    // For group invoicing only non-invoiced BLs can be selected.
    // For bulk print all rows are eligible.
    const eligible = canGroupInvoice ? filtered.filter((d) => !d.isInvoiced) : filtered;
    if (eligible.length > 0 && eligible.every((d) => selected.has(d.id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(eligible.map((d) => d.id)));
    }
  };

  const handleBulkPrint = async () => {
    if (selectedDocs.length === 0 || activeTemplates.length === 0) return;
    const tpl = activeTemplates[0]!;
    setBulkPrinting(true);
    try {
      const items = selectedDocs
        .map((doc) => {
          const client = resolveDocumentCustomer(doc, clients) ?? {
            code: doc.clientId || null,
            name: doc.clientId || 'Client',
            type: 'individual' as const,
            taxId: null,
            address: null,
            city: null,
            phone: null,
            fax: null,
            carRegistrationNumber: null,
          };
          return {
            scope: 'sales' as const,
            docType: doc.docType,
            data: {
              docType: doc.docType,
              number: doc.number,
              docDate: doc.docDate,
              validityDate: doc.validityDate,
              client: {
                code: client.code ?? '', name: client.name, type: client.type,
                taxId: 'taxId' in client ? client.taxId : null,
                address: client.address ?? null,
                city: client.city ?? null,
                phone: client.phone ?? null,
                fax: 'fax' in client ? client.fax : null,
                carRegistrationNumber: doc.carRegistrationNumber ?? ('carRegistrationNumber' in client ? client.carRegistrationNumber : null),
              },
              lines: (doc.lines ?? []).map((l, i) => ({
                lineNumber: i + 1, description: l.description, quantity: l.quantity,
                unitPriceHt: l.unitPriceHt, discountPct: l.discountPct, vatRate: l.vatRate,
                totalHt: l.totalHt,
                articleCode: articles?.find((a) => a.id === l.articleId)?.code ?? null,
              })),
              totalHt: doc.totalHt,
              totalDiscount: doc.totalDiscount,
              totalVat: doc.totalVat,
              totalTtc: doc.totalTtc,
              withholdingAmount: doc.withholdingAmount,
              stampAmount: doc.stampAmount,
              netToPay: doc.netToPay,
              notes: doc.notes,
            },
          };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null);

      if (items.length === 0) return;
      await printBatchWithTemplate(tpl, companyInfoForPdf(companySettings), items);
    } finally {
      setBulkPrinting(false);
    }
  };

  // Non-invoiceable articles in selected BLs
  const groupNonInvoiceableNames = useMemo(() => {
    const names: string[] = [];
    for (const doc of selectedDocs) {
      for (const l of doc.lines ?? []) {
        if (l.articleId && articleMap.get(l.articleId)?.isInvoiceable === false) {
          names.push(l.description);
        }
      }
    }
    return names;
  }, [selectedDocs, articleMap]);

  const handleGroupInvoice = async () => {
    if (!groupValidation.valid) return;
    setGroupInvoicing(true);
    try {
      const fullSelectedDocs = await Promise.all(
        selectedDocs.map(async (doc) => {
          if (doc.lines?.length) return doc;
          const fullDoc = await ds.sales.getDocumentById(doc.id);
          return fullDoc ?? doc;
        }),
      );
      // Merge all lines — filter non-invoiceable unless user chose to keep them
      const rawLines = fullSelectedDocs.flatMap((doc) => doc.lines ?? []);
      const filteredRawLines = groupKeepNonInvoiceable
        ? rawLines
        : rawLines.filter((l) => {
            if (!l.articleId) return true;
            return articleMap.get(l.articleId)?.isInvoiceable !== false;
          });

      const allLines = filteredRawLines.map((l) => {
        const lineInput = {
          quantity: l.quantity,
          unitPriceHt: l.unitPriceHt,
          discountPct: l.discountPct,
          vatRate: l.vatRate,
        };
        const lt = calculateLineTotals(lineInput);
        return {
          articleId: l.articleId ?? undefined,
          description: l.description,
          ...lineInput,
          purchasePrice: l.purchasePrice ?? 0,
          totalHt: lt.totalHt,
          totalTtc: lt.totalTtc,
        };
      });

      const firstDoc = fullSelectedDocs[0]!;
      const client = clientMap.get(firstDoc.clientId);
      const docTotals = calculateDocumentTotals(
        allLines,
        client?.type ?? 'individual',
        { threshold: 1000, rate: 1 },
        STAMP_AMOUNT,
      );

      const parentDocIds = fullSelectedDocs.map((d) => d.id);

      await createMutation.mutateAsync({
        docType: 'invoice',
        clientId: firstDoc.clientId,
        carRegistrationNumber: firstDoc.carRegistrationNumber ?? client?.carRegistrationNumber ?? null,
        docDate: todayISO(),
        parentDocId: parentDocIds[0],
        parentDocIds,
        lines: allLines,
        ...docTotals,
      });

      // Mark all selected BLs as invoiced
      await Promise.all(parentDocIds.map((id) => markInvoicedMutation.mutateAsync(id)));

      setSelected(new Set());
      setGroupInvoiceOpen(false);
      navigate('/sales/invoices');
    } finally {
      setGroupInvoicing(false);
    }
  };

  const titleKey = DOC_TYPE_PLURAL_LABELS[docType];
  const targets = TRANSFORM_TARGETS[docType];

  const totalColSpan = visibleColumns.length + (showSelectionColumn ? 2 : 1);

  const renderCell = (doc: SalesDocument, colKey: string) => {
    const client = clientMap.get(doc.clientId);
    const documentCustomer = resolveDocumentCustomer(doc, clients);
    if (colKey === 'client') {
      return (
        <div className="min-w-0">
          <p className="truncate">{documentCustomer?.name ?? client?.name ?? '-'}</p>
          {documentCustomer && documentCustomer.source !== 'local' && (
            <p className="text-[11px] text-muted-foreground">
              {t(documentCustomer.sourceLabelKey, documentCustomer.sourceLabel)}
            </p>
          )}
        </div>
      );
    }
    switch (colKey) {
      case 'number':
        return <span className="font-medium">{doc.number}</span>;
      case 'client':
        return client?.name ?? '—';
      case 'representative':
        return doc.representativeId ? representativeMap.get(doc.representativeId) ?? '—' : '—';
      case 'carRegistrationNumber':
        return doc.carRegistrationNumber ?? client?.carRegistrationNumber ?? '—';
      case 'docDate':
        return formatDate(doc.docDate);
      case 'validityDate':
        return doc.validityDate ? formatDate(doc.validityDate) : '—';
      case 'parentDoc': {
        const parentIds = doc.parentDocIds?.length ? doc.parentDocIds : doc.parentDocId ? [doc.parentDocId] : [];
        if (parentIds.length === 0) return '—';
        if (parentIds.length === 1) return parentIds[0]!.slice(0, 8);
        return `${parentIds.length}`;
      }
      case 'channel':
        return doc.channel ? t(`sales.channels.${doc.channel}`, { defaultValue: doc.channel }) : '—';
      case 'proCommandeCode':
        return doc.proCommandeCode ?? '—';
      case 'totalHt':
        return formatPrice(doc.totalHt);
      case 'totalDiscount':
        return doc.totalDiscount > 0 ? formatPrice(doc.totalDiscount) : '—';
      case 'totalVat':
        return formatPrice(doc.totalVat);
      case 'totalTtc':
        return formatPrice(doc.totalTtc);
      case 'withholdingAmount':
        return doc.withholdingAmount > 0 ? formatPrice(doc.withholdingAmount) : '—';
      case 'stampAmount':
        return doc.stampAmount > 0 ? formatPrice(doc.stampAmount) : '—';
      case 'deliveryFee':
        return doc.deliveryFee > 0 ? formatPrice(doc.deliveryFee) : '—';
      case 'netToPay':
        return formatPrice(doc.netToPay);
      case 'isInvoiced':
        return doc.isInvoiced ? (
          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400">
            {t('sales.isInvoiced')}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">
            {t('sales.notInvoiced')}
          </Badge>
        );
      case 'paymentStatus': {
        const allocated = allocatedByDocId.get(doc.id) ?? 0;
        const status = computePaymentStatus(doc.netToPay ?? doc.totalTtc, allocated);
        return <PaymentStatusBadge status={status} />;
      }
      case 'benefit': {
        const lines = doc.lines ?? [];
        if (lines.length === 0) return <span className="text-muted-foreground">—</span>;
        const benefit = calculateDocumentBenefit(doc, articleMap);
        if (!benefit.hasKnownPurchasePrice) return <span className="text-muted-foreground">—</span>;
        const rounded = benefit.totalBenefit;
        return (
          <span className={`${rounded >= 0 ? 'text-green-600' : 'text-destructive'}`}>
            {rounded >= 0 ? '+' : ''}{formatPrice(rounded)}
          </span>
        );
      }
      case 'notes':
        return doc.notes ? <span className="truncate max-w-[200px] inline-block">{doc.notes}</span> : '—';
      case 'createdAt':
        return formatDateTime(doc.createdAt);
      case 'updatedAt':
        return formatDateTime(doc.updatedAt);
      default:
        return '—';
    }
  };

  // Group invoice totals for dialog
  const groupTotalTtc = selectedDocs.reduce((sum, d) => sum + d.totalTtc, 0);
  const groupTotalHt = selectedDocs.reduce((sum, d) => sum + d.totalHt, 0);

  const invoicedFilterOptions = useMemo(() => [
    { value: 'true', label: t('sales.isInvoiced') },
    { value: 'false', label: t('sales.notInvoiced') },
  ], [t]);

  const paymentStatusFilterOptions = useMemo(() => ([
    'paid',
    'partially_paid',
    'unpaid',
  ] as PaymentStatus[]).map((status) => ({
    value: status,
    label: t(`payments.status.${status}`),
  })), [t]);

  const hasActiveFilters = Boolean(searchNumber || filterClientId || dateFrom || dateTo || filterInvoiced || filterPaymentStatus || hasColumnFilters);
  const clearFilters = () => {
    setSearchNumber('');
    setFilterClientId('');
    setDateFrom('');
    setDateTo('');
    setFilterInvoiced('');
    setFilterPaymentStatus('');
    clearColumnFilters();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t(titleKey)}
        actions={
          <Button onClick={() => navigate(`${basePath}/new`)}>
            <Plus data-icon="inline-start" size={16} />
            {t('sales.newDocument', { type: t(DOC_TYPE_LABELS[docType]) })}
          </Button>
        }
      />

      <Card className="transition-all duration-200">
        <CardHeader className="pb-3">
          <FilterBar
            onClear={hasActiveFilters ? clearFilters : undefined}
            clearLabel={t('common.clearFilters')}
            rightSlot={
              <>
                {selected.size >= 1 && activeTemplates.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkPrint}
                    disabled={bulkPrinting}
                  >
                    <Printer size={14} className="me-1.5" />
                    {t('sales.printSelected')} ({selected.size})
                  </Button>
                )}
                {canGroupInvoice && selected.size >= 2 && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setGroupInvoiceOpen(true)}
                    disabled={!groupValidation.valid}
                  >
                    <Receipt size={14} className="me-1.5" />
                    {t('sales.groupInvoice')} ({selected.size})
                  </Button>
                )}
                <ColumnConfigButton onClick={() => setColConfigOpen(true)} />
              </>
            }
          >
            <FilterField label={t('sales.number')} minWidth={180}>
              <div className="relative">
                <Search size={14} className="absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none start-2.5" />
                <Input placeholder={t('sales.number')} value={searchNumber} onChange={(e) => setSearchNumber(e.target.value)} className="ps-8 h-8" />
              </div>
            </FilterField>
            <FilterField label={t('sales.client')} minWidth={240}>
              <SearchableSelect
                options={(clients ?? []).map((c) => ({ value: c.id, label: c.name, sublabel: c.code }))}
                value={filterClientId}
                onChange={setFilterClientId}
                label={t('sales.client')}
                placeholder={t('common.all')}
                searchPlaceholder={t('common.search')}
              />
            </FilterField>
            <FilterField label={t('common.dateFrom')} minWidth={150}>
              <DatePicker value={dateFrom} onChange={setDateFrom} placeholder={t('common.dateFrom')} />
            </FilterField>
            <FilterField label={t('common.dateTo')} minWidth={150}>
              <DatePicker value={dateTo} onChange={setDateTo} placeholder={t('common.dateTo')} />
            </FilterField>
            {showInvoicedFilter && (
              <FilterField label={t('sales.isInvoiced')} minWidth={170}>
                <SearchableSelect
                  options={invoicedFilterOptions}
                  value={filterInvoiced}
                  onChange={setFilterInvoiced}
                  label={t('sales.isInvoiced')}
                  placeholder={t('common.all')}
                  searchPlaceholder={t('common.search')}
                />
              </FilterField>
            )}
            {showPaymentStatusFilter && (
              <FilterField label={t('payments.statusLabel')} minWidth={190}>
                <SearchableSelect
                  options={paymentStatusFilterOptions}
                  value={filterPaymentStatus}
                  onChange={setFilterPaymentStatus}
                  label={t('payments.statusLabel')}
                  placeholder={t('common.all')}
                  searchPlaceholder={t('common.search')}
                />
              </FilterField>
            )}
            <ColumnTextFilterFields
              rows={pageDocuments}
              columns={toolbarColumnFilters}
              filters={columnFilters}
              onFilterChange={setColumnFilter}
              allLabel={t('common.all')}
              searchPlaceholder={t('common.search')}
            />
          </FilterBar>

          {canGroupInvoice && selected.size >= 2 && !groupValidation.valid && (
            <p className="text-xs text-destructive mt-2">{t(groupValidation.reason)}</p>
          )}
        </CardHeader>

        <CardContent className="pt-0">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-b border-border">
              {showSelectionColumn && (
                <TableHead className="h-11 w-10 bg-muted/50 px-3 first:ps-3 sm:px-4 sm:first:ps-5">
                  <Checkbox
                    checked={(() => {
                      const eligible = canGroupInvoice ? filtered.filter((d) => !d.isInvoiced) : filtered;
                      return eligible.length > 0 && eligible.every((d) => selected.has(d.id));
                    })()}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
              )}
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={`h-11 bg-muted/50 px-3 text-xs font-medium select-none first:ps-3 last:pe-3 sm:px-4 sm:text-sm sm:first:ps-5 sm:last:pe-5 ${
                    isNumericColumn(col.key) ? 'text-end' :
                    isCenterColumn(col.key) ? 'text-center' : 'text-start'
                  }`}
                >
                  {t(col.labelKey)}
                </TableHead>
              ))}
              <TableHead className="h-11 w-10 bg-muted/50 px-3 last:pe-3 sm:px-4 sm:last:pe-5" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {hasLoadError ? (
              <TableRow className="border-0">
                <TableCell colSpan={totalColSpan} className="px-4 py-10 text-center text-sm text-destructive">
                  {t('common.errorLoading')}
                </TableCell>
              </TableRow>
            ) : isCheckingDocuments || isWaitingForPageDocuments ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`sk-${i}`} className="border-b border-border/60 last:border-b-0">
                  {Array.from({ length: totalColSpan }).map((_, j) => (
                    <TableCell key={j} className="px-3 py-2 sm:px-4 sm:py-3">
                      <Skeleton className="h-4 w-full max-w-[160px] bg-muted/70" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : totalDocuments === 0 || filtered.length === 0 ? (
              <TableRow className="border-0">
                <TableCell colSpan={totalColSpan} className="px-4 py-10 text-center">
                  <EmptyState
                    icon={<FileText size={18} />}
                    title={t('common.noResults')}
                    action={<Button variant="outline" size="sm" onClick={() => navigate(`${basePath}/new`)}>
                        <Plus data-icon="inline-start" size={14} />
                        {t('sales.newDocument', { type: t(DOC_TYPE_LABELS[docType]) })}
                      </Button>
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((doc) => (
                <TableRow
                  key={doc.id}
                  className={`border-0 transition-colors cursor-pointer ${selected.has(doc.id) ? 'bg-accent' : 'hover:bg-muted/50'}`}
                  onClick={() => navigate(`${basePath}/${doc.id}`)}
                >
                  {showSelectionColumn && (
                    <TableCell className="px-3 py-2 sm:px-4 sm:py-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(doc.id)}
                        onCheckedChange={() => toggleSelect(doc.id)}
                      />
                    </TableCell>
                  )}
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={`px-3 py-2 text-xs first:ps-3 last:pe-3 sm:px-4 sm:py-3 sm:text-sm sm:first:ps-5 sm:last:pe-5 ${
                        isNumericColumn(col.key) ? 'text-end' :
                        isCenterColumn(col.key) ? 'text-center' : undefined
                      }`}
                    >
                      {renderCell(doc, col.key)}
                    </TableCell>
                  ))}
                  <TableCell className="px-3 py-2 sm:px-4 sm:py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon-xs"><MoreHorizontal size={14} /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[220px]">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`${basePath}/${doc.id}`); }}>
                          <Pencil size={14} />{t('common.edit')}
                        </DropdownMenuItem>
                        {targets.length > 0 && (
                          <>
                            <DropdownMenuSeparator />
                            {targets.map((target) => (
                              <DropdownMenuItem key={target} onClick={(e) => { e.stopPropagation(); handleTransform(doc, target); }}>
                                <ArrowRight size={14} className="rtl:rotate-180" />
                                {t('sales.transformTo')} {t(DOC_TYPE_LABELS[target])}
                              </DropdownMenuItem>
                            ))}
                          </>
                        )}
                        {docType === 'invoice' && (() => {
                          const client = clientMap.get(doc.clientId);
                          if (!client) return null;
                          return (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                createCertificate('sale',
                                  { id: doc.id, number: doc.number, docDate: doc.docDate, totalHt: doc.totalHt, totalVat: doc.totalVat, totalTtc: doc.totalTtc },
                                  { id: client.id, type: client.type });
                              }}>
                                <FileText size={14} />
                                {t('withholding.createCertificate')}
                              </DropdownMenuItem>
                            </>
                          );
                        })()}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onClick={(e) => { e.stopPropagation(); setToDelete(doc); setDeleteOpen(true); }}>
                          <Trash2 size={14} />{t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div className="flex flex-col gap-3 border-t border-border px-3 py-3 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            {isCheckingDocuments
              ? t('common.checkingData')
              : t('common.paginationSummary', { start: pageStart, end: pageEnd, total: totalDocuments })}
            {!isCheckingDocuments && (isFetching || isCountFetching) && (
              <span className="ms-2">{t('common.loading')}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              disabled={page <= 1 || isCheckingDocuments || isWaitingForPageDocuments}
            >
              <ChevronLeft data-icon="inline-start" size={14} className="rtl:rotate-180" />
              {t('common.previousPage')}
            </Button>
            <span className="min-w-24 text-center">
              {t('common.pageOf', { page, totalPages })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
              disabled={page >= totalPages || isCheckingDocuments || isWaitingForPageDocuments}
            >
              {t('common.nextPage')}
              <ChevronRight data-icon="inline-end" size={14} className="rtl:rotate-180" />
            </Button>
          </div>
        </div>
        </CardContent>
      </Card>

      {/* Delete confirm dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('sales.deleteConfirm')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('sales.deleteWarning')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group invoice confirm dialog */}
      <Dialog open={groupInvoiceOpen} onOpenChange={setGroupInvoiceOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('sales.groupInvoice')}</DialogTitle>
            <DialogDescription>{t('sales.groupInvoice.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm font-medium">{t('sales.groupInvoice.selectedBLs')}</p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {selectedDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
                  <span className="font-medium">{doc.number}</span>
                  {formatPrice(doc.totalTtc)} DT
                </div>
              ))}
            </div>
            <Separator />
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('sales.totalHt')}</span>
              <span className="font-medium">{formatPrice(groupTotalHt)} DT</span>
            </div>
            <div className="flex justify-between text-base font-bold">
              <span>{t('sales.totalTtc')}</span>
              {formatPrice(groupTotalTtc)} DT
            </div>
          </div>
          {/* Non-invoiceable alert */}
          {groupNonInvoiceableNames.length > 0 && (
            <div className="rounded-lg border border-yellow-500/50 bg-yellow-50 dark:bg-yellow-950/20 p-3 space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle size={16} className="text-yellow-600" />
                {t('sales.nonInvoiceableAlert.title')}
              </p>
              <p className="text-xs text-muted-foreground">{t('sales.nonInvoiceableAlert.description')}</p>
              <div className="flex flex-wrap gap-1">
                {groupNonInvoiceableNames.map((name, i) => (
                  <Badge key={i} variant="outline" className="text-yellow-700 text-xs">{name}</Badge>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="keepNonInv"
                  checked={groupKeepNonInvoiceable}
                  onCheckedChange={(v) => setGroupKeepNonInvoiceable(Boolean(v))}
                />
                <label htmlFor="keepNonInv" className="text-xs cursor-pointer">{t('sales.nonInvoiceableAlert.keepAll')}</label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setGroupInvoiceOpen(false); setGroupKeepNonInvoiceable(false); }}>{t('common.cancel')}</Button>
            <Button onClick={handleGroupInvoice} disabled={groupInvoicing}>
              <Receipt size={14} className="me-1.5" />
              {groupInvoicing ? t('common.loading') : t('sales.groupInvoice.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column config dialog */}
      <ColumnConfigDialog
        open={colConfigOpen}
        onOpenChange={setColConfigOpen}
        visibleCols={visibleKeys}
        allColumns={applicableColumns}
        toggleCol={toggleColumn}
        moveCol={moveColumn}
        resetCols={resetToDefault}
      />

      <CertificateConfirmationDialog />
    </div>
  );
}

function getBasePath(docType: SalesDocType): string {
  const map: Record<SalesDocType, string> = {
    quote: '/sales/quotes',
    order: '/sales/orders',
    delivery: '/sales/deliveries',
    invoice: '/sales/invoices',
    credit_note: '/sales/credit-notes',
  };
  return map[docType];
}
